import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "../../api/axios";
import { toast } from "react-toastify";
import "./Inspiration.scss";
import { getAllTemplatesCategoriesRoute } from "../../api/routes";
import { TOP_LEVEL_NAV_ROUTES } from "../../config/NavRoutes";
import PageTopBanner from "../../components/PageTopBanner/PageTopBanner";
import toDisplayAssetUrl from "../../helpers/DisplayAssetUrl";
import { templateHasCategory } from '../../helpers/templateCategories';

// ─── Individual template card ────────────────────────────────────────────────
const TemplateCard = ({ template, onClick }) => (
    <div className="inspo-card button-active" onClick={() => onClick(template)}>
        <div
            className="inspo-card-image"
            style={template.templateImage ? { backgroundImage: `url(${toDisplayAssetUrl(template.templateImage)})` } : {}}
        />
        <div className="inspo-card-info">
            <span className="inspo-card-title">{template.name}</span>
            <span className="inspo-card-sub">{template.featureType || template.templateType}</span>
        </div>
    </div>
);

// ─── Category row ────────────────────────────────────────────────────────────
const CATEGORY_LIMIT = 9;

const CategoryRow = ({ category, templates, onCardClick, onViewAll, isViewAll }) => {
    if (!templates || templates.length === 0) return null;
    const limited = !isViewAll && templates.length > CATEGORY_LIMIT;
    const visibleTemplates = limited ? templates.slice(0, CATEGORY_LIMIT) : templates;
    return (
        <div className="inspo-category-row">
            <div className="inspo-category-header">
                <h3 className="inspo-category-title">{category.name}</h3>
                {limited && (
                    <button className="inspo-view-all-btn button-active" onClick={() => onViewAll(category)}>
                        <span className="inspo-view-all-count">{templates.length} items</span>
                        {" View All"}
                    </button>
                )}
            </div>
            <div className={isViewAll ? "inspo-card-grid" : "inspo-card-scroll"}>
                {visibleTemplates.map(t => (
                    <TemplateCard key={t._id} template={t} onClick={onCardClick} />
                ))}
            </div>
        </div>
    );
};

// ─── Main Inspiration component ───────────────────────────────────────────────
const Inspiration = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [templateCategories, setTemplateCategories] = useState([]);
    const [allTemplates, setAllTemplates] = useState([]);
    const [featureTypes, setFeatureTypes] = useState([]);
    const [loading, setLoading] = useState(true);

    // 'feature' or 'campaign'
    const [activeTab, setActiveTab] = useState("feature");
    // null = all categories, otherwise category._id
    const [activeCategoryId, setActiveCategoryId] = useState(null);
    // null = all feature types, otherwise featureType string
    const [activeFeatureType, setActiveFeatureType] = useState(null);
    // search term
    const [searchTerm, setSearchTerm] = useState("");

    // Read incoming location state – allows the Home page search input to pass
    // a pre-selected tab when navigating here.
    useEffect(() => {
        if (location.state?.tab) {
            setActiveTab(location.state.tab);
        }
    }, [location.state]);

    useEffect(() => {
        setLoading(true);
        axios
            .get(getAllTemplatesCategoriesRoute, { withCredentials: true })
            .then(res => {
                const data = res.data?.data || {};
                setTemplateCategories(data.categories || []);
                setAllTemplates(data.templates || []);
                setFeatureTypes(data.featureTypes || []);
            })
            .catch(() => toast.error("Failed to load templates"))
            .finally(() => setLoading(false));
    }, []);

    // Navigate to CampaignSetup with the clicked template pre-selected
    const handleCardClick = (template) => {
        navigate(TOP_LEVEL_NAV_ROUTES.CAMPAIGN_SETUP, {
            state: {
                fromInspiration: true,
                templateId: template._id,
                templateType: template.templateType,
            },
        });
    };

    // "View all" on a category row → filter the view to just that category
    const handleViewAll = (category) => {
        setActiveCategoryId(category._id);
        setActiveFeatureType(null);
    };

    const clearCategoryFilter = () => {
        setActiveCategoryId(null);
        setActiveFeatureType(null);
    };

    // Templates scoped to the current tab
    const tabTemplates = useMemo(
        () => allTemplates.filter(t => t.templateType === activeTab),
        [allTemplates, activeTab]
    );

    // Feature-type chips for the current tab (only meaningful for 'feature' tab)
    const availableFeatureTypes = useMemo(() => {
        const types = [...new Set(tabTemplates.map(t => t.featureType).filter(Boolean))];
        return featureTypes.filter(ft => types.includes(ft));
    }, [tabTemplates, featureTypes]);

    // Build the list of categories + their filtered templates to display
    const displayData = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase().trim();

        return templateCategories
            .map(cat => {
                let templates = tabTemplates.filter((template) => templateHasCategory(template, cat._id));

                if (activeCategoryId && cat._id !== activeCategoryId) return null;
                if (activeFeatureType) templates = templates.filter(t => t.featureType === activeFeatureType);
                if (lowerSearch) {
                    templates = templates.filter(
                        t =>
                            t.name?.toLowerCase().includes(lowerSearch) ||
                            t.featureType?.toLowerCase().includes(lowerSearch)
                    );
                }

                return templates.length > 0 ? { category: cat, templates } : null;
            })
            .filter(Boolean);
    }, [templateCategories, tabTemplates, activeCategoryId, activeFeatureType, searchTerm]);

    const activeCategory = activeCategoryId
        ? templateCategories.find(c => c._id === activeCategoryId)
        : null;

    return (
        <div className="content-page-container inspo-page">
            <PageTopBanner />

            {/* ── Page header ── */}
            <div className="content-page-top">
                <div className="fl-column grow">
                    <div className="inspo-top-row">
                        <h4 className="page-title">Inspiration</h4>
                        <div className="spacing-line" />
                        <input
                            type="text"
                            className="inspo-search-input"
                            placeholder="Search by feature, campaign names or content keywords"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* ── Tabs ── */}
                    <div className="inspo-tabs">
                        <button
                            className={`inspo-tab${activeTab === "feature" ? " active" : ""}`}
                            onClick={() => {
                                setActiveTab("feature");
                                setActiveCategoryId(null);
                                setActiveFeatureType(null);
                            }}
                        >
                            Feature templates
                        </button>
                        <button
                            className={`inspo-tab${activeTab === "campaign" ? " active" : ""}`}
                            onClick={() => {
                                setActiveTab("campaign");
                                setActiveCategoryId(null);
                                setActiveFeatureType(null);
                            }}
                        >
                            Campaign templates
                        </button>
                    </div>

                    {/* ── Feature-type filter chips (feature tab only) ── */}
                    {activeTab === "feature" && availableFeatureTypes.length > 0 && (
                        <div className="inspo-chips">
                            {availableFeatureTypes.map(ft => (
                                <button
                                    key={ft}
                                    className={`inspo-chip button-active${activeFeatureType === ft ? " active" : ""}`}
                                    onClick={() => {
                                        setActiveFeatureType(prev => (prev === ft ? null : ft));
                                    }}
                                >
                                    {ft}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* ── Active category breadcrumb ── */}
                    {activeCategory && (
                        <div className="inspo-category-filter-bar">
                            <span className="inspo-category-filter-label">
                                Showing: <strong>{activeCategory.name}</strong>
                            </span>
                            <button className="inspo-clear-filter button-active" onClick={clearCategoryFilter}>
                                ✕ Show all
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Content ── */}
            <div className="content-page-content-container">
                <div className="content-page-main-content inspo-main-content">
                    {loading ? (
                        <div className="inspo-loading" />
                    ) : displayData.length === 0 ? (
                        <div className="inspo-empty">
                            No templates found{searchTerm ? ` for "${searchTerm}"` : ""}.
                        </div>
                    ) : (
                        displayData.map(({ category, templates }) => (
                            <CategoryRow
                                key={category._id}
                                category={category}
                                templates={templates}
                                onCardClick={handleCardClick}
                                onViewAll={handleViewAll}
                                isViewAll={activeCategoryId === category._id}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default Inspiration;
